/**
 * Eingabearten der Einstellungen und ihre Prüfregeln – nur Zod, ohne Datenbank.
 * Jede Einstellung meldet eine Eingabeart (`SettingInput`, reines JSON) an; daraus entsteht mit
 * `schemaForInput` das Zod-Schema. Dasselbe Schema nutzen der Command `settings.setSetting` und das
 * Formular in der Web-App (Vorprüfung vor dem Senden). Import in der Web-App über `@till-erp/settings/values`.
 */
import { z } from 'zod';
import { CURRENCY_INVALID_TEXT, normalizeCurrencyCode } from '@till-erp/platform/currencies';
import { isIsoDate } from '@till-erp/platform/dates';
import { isValidBic, isValidIban } from './bank-codes.ts';

export const EMAIL_REQUIRED_TEXT = 'Bitte gib eine E-Mail-Adresse ein, an die Alarme gehen sollen.';
export const EMAIL_INVALID_TEXT = 'Bitte eine gültige E-Mail-Adresse eingeben.';
export const BOOLEAN_INVALID_TEXT = 'Bitte wähle „an“ oder „aus“.';
export const INTEGER_INVALID_TEXT = 'Bitte eine ganze Zahl eingeben.';
export const MONEY_INVALID_TEXT = 'Bitte einen Betrag wie 4,90 eingeben.';
export const REQUIRED_TEXT = 'Bitte ausfüllen.';
export const VALID_FROM_INVALID_TEXT = 'Bitte ein gültiges Datum eingeben (JJJJ-MM-TT, z. B. 2027-02-01).';
/** Einstellungen gelten immer ab dem Speicherzeitpunkt (Rudolf F3, Story 1.26); ein mitgeschicktes Datum wird abgelehnt. */
export const SETTINGS_ALWAYS_NOW_TEXT = 'Einstellungen gelten immer ab dem Speichern.';

/** Spalte einer Tabellen-Einstellung (z. B. Versandkosten je Versandart). */
export type TableColumn =
  | { key: string; label: string; type: 'text'; required?: boolean; maxLength?: number }
  | { key: string; label: string; type: 'integer'; required?: boolean; min?: number; max?: number; unit?: string }
  /** Geld als ganze Cent + Währung (nie Gleitkomma); Eingabe deutsch, z. B. „4,90“. */
  | { key: string; label: string; type: 'money'; required?: boolean; currency: string }
  /**
   * Auswahl aus festen Werten (z. B. Shopware-Status). Gespeichert wird `value`, angezeigt `label`. Ohne „Pflicht“
   * bedeutet leer (`''`) „keine Auswahl“; dafür zeigt die Oberfläche `emptyLabel` (Standard „–“).
   */
  | { key: string; label: string; type: 'select'; required?: boolean; options: readonly SelectOption[]; emptyLabel?: string };

/** Wert einer Auswahl-Spalte. */
export interface SelectOption {
  value: string;
  label: string;
}

export const SELECT_INVALID_TEXT = 'Bitte einen Wert aus der Liste wählen.';

export const USER_INVALID_TEXT = 'Bitte eine Person aus der Liste wählen.';

/** Eingabeart einer Einstellung – bestimmt Prüfregel und Formular. */
export type SettingInput =
  | { kind: 'email'; fieldLabel: string; requiredMessage?: string }
  /**
   * Freitext; mit `format` geprüft (leer bleibt erlaubt, außer bei `required`): `email`, `phone` (Ziffern, Leerzeichen,
   * + ( ) / -), `url` (Internetadresse, z. B. www.tilltools.de), `iban` (Prüfziffer mod 97, gespeichert ohne
   * Leerzeichen in Großbuchstaben), `bic` (8 oder 11 Zeichen, Großbuchstaben). `multiline` (Story 5.42): mehrzeiliges
   * Feld, Zeilenumbrüche bleiben erhalten (z. B. Inventuranweisung); nicht zusammen mit `format`.
   */
  | { kind: 'text'; fieldLabel: string; required?: boolean; maxLength?: number; format?: TextFormat; multiline?: boolean }
  | { kind: 'boolean'; switchLabel: string; /** Hinweis, solange „an“ gewählt, aber noch nicht gespeichert ist. */ onHint?: string }
  /**
   * Ganze Zahl. Mit `emptyLabel` (Story 18.5, z. B. Löschfristen) darf der Wert leer bleiben (`null`, „nicht festgelegt“):
   * dann ist er keine fehlende Pflichtangabe, und die Oberfläche zeigt für leer `emptyLabel`.
   */
  | { kind: 'integer'; fieldLabel: string; min?: number; max?: number; unit?: string; emptyLabel?: string }
  /**
   * Geldbetrag in fester Währung (Story 7.2, z. B. „MRN-Pflicht über Warenwert“): gespeichert als ganze Cent + Währung
   * (`MoneyValue`, nie Gleitkomma), Eingabe deutsch, z. B. „1.000,00“. Pflichtfeld. EUR (Rudolf 03.10.2026: interne
   * Grenzen und Preise in EUR); USD nur für KI-Kosten wie `ai_budget` (Story 31.2: Kosten-Grenzen der Agenten in $).
   * `parseMoney` rechnet mit zwei Nachkommastellen.
   */
  | { kind: 'money'; fieldLabel: string; currency: 'EUR' | 'USD' }
  /** Genau ein Wert aus einer festen Liste (gespeichert wird `value`, angezeigt `label`). */
  | { kind: 'select'; fieldLabel: string; options: readonly SelectOption[] }
  /**
   * Prozentwert mit wenigen Nachkommastellen (Story 10.15, z. B. Mindestmarge „20“ oder „99,9“): gespeichert als
   * Dezimaltext mit Punkt ohne überflüssige Nullen (nie Gleitkomma), Eingabe deutsch mit Komma. `min` und
   * `maxExclusive` als Dezimaltext; `invalidText` ersetzt jeden Prüfsatz (Form, Stellen und Bereich). `unit` (Story 11.18,
   * Standard „%“): andere Einheit desselben Dezimalfelds, z. B. „kg“ für Gewichte („0,05 kg“, gespeichert „0.05“).
   */
  | { kind: 'percent'; fieldLabel: string; fractionDigits: number; min?: string; maxExclusive?: string; invalidText?: string; unit?: string }
  /** Währungscode nach ISO 4217 (zentrale Liste in platform); Eingabe wird in Großbuchstaben gespeichert. */
  | { kind: 'currency'; fieldLabel: string }
  /**
   * Eine Person aus den Nutzern (Story 12.11, z. B. Vertretung für die Sendeentscheidung): gespeichert wird die
   * `platform.app_user.id` oder `null` (niemand); die Oberfläche wählt aus `GET /settings/{key}/user-options` (aktive
   * Admins und Nutzer der Buchhaltung) und zeigt für leer `emptyLabel`.
   */
  | { kind: 'user'; fieldLabel: string; emptyLabel: string }
  | {
      kind: 'table';
      columns: TableColumn[];
      maxRows?: number;
      /** Spalte, deren Wert in jeder Zeile nur einmal vorkommen darf (z. B. das ERP-Ereignis der Statustabelle). */
      uniqueBy?: string;
    };

/** Prüfregel eines Freitexts (Story 2.14: Kontaktangaben und Bankverbindung unter Einstellungen › Firma). */
export type TextFormat = 'email' | 'phone' | 'url' | 'iban' | 'bic' | 'date';

export const TEXT_FORMAT_TEXTS: Record<TextFormat, string> = {
  email: EMAIL_INVALID_TEXT,
  phone: 'Bitte eine Telefonnummer eingeben, z. B. +49 821 000000-0 (nur Ziffern, Leerzeichen und + ( ) / -).',
  url: 'Bitte eine Internetadresse eingeben, z. B. www.tilltools.de oder https://www.tilltools.de.',
  iban: 'Die IBAN stimmt nicht: Bitte Länderkennung, Prüfziffer und Kontonummer prüfen, z. B. DE89 3704 0044 0532 0130 00.',
  bic: 'Bitte eine BIC mit 8 oder 11 Zeichen eingeben (4 Buchstaben Bank, 2 Buchstaben Land, 2 Zeichen Ort, ggf. 3 Zeichen Filiale), z. B. COBADEFFXXX.',
  date: 'Bitte ein gültiges Datum eingeben, z. B. 01.02.2027.',
};

export { isValidBic, isValidIban } from './bank-codes.ts';

/** Freitext nach `format` prüfen und vereinheitlichen; leer bleibt leer. */
function formatText(format: TextFormat, value: string): { ok: true; value: string } | { ok: false } {
  if (value === '') return { ok: true, value };
  switch (format) {
    case 'email':
      return z.email().safeParse(value).success ? { ok: true, value } : { ok: false };
    case 'phone':
      // Nur Ziffern, Leerzeichen und + ( ) / -, mindestens 6 Ziffern (Plausibilität, keine Fachregel); Länge wie das
      // Formularfeld (maxLength der Einstellung, höchstens 40).
      return /^\+?[0-9(][0-9 ()/-]{3,39}$/.test(value) && (value.match(/\d/g)?.length ?? 0) >= 6 ? { ok: true, value } : { ok: false };
    case 'url':
      return /^(https?:\/\/)?[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}(\/\S*)?$/.test(value) ? { ok: true, value } : { ok: false };
    case 'iban': {
      const iban = value.replace(/\s+/g, '').toUpperCase();
      return isValidIban(iban) ? { ok: true, value: iban } : { ok: false };
    }
    case 'bic': {
      const bic = value.replace(/\s+/g, '').toUpperCase();
      return isValidBic(bic) ? { ok: true, value: bic } : { ok: false };
    }
    case 'date': {
      // Datum (Story 4.23): „01.02.2027“ oder „2027-02-01“, gespeichert als ISO-Datum.
      const german = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(value);
      const iso = german ? `${german[3]}-${german[2]!.padStart(2, '0')}-${german[1]!.padStart(2, '0')}` : value;
      return isIsoDate(iso) ? { ok: true, value: iso } : { ok: false };
    }
  }
}

/** Gespeicherter Geldbetrag in einer Tabellenzelle. */
export interface MoneyValue {
  /** Ganze Cent (kleinste Einheit). */
  amountMinor: number;
  currency: string;
}

const MONEY_PATTERN = /^(\d{1,3}(?:\.\d{3})+|\d+)(?:,(\d{1,2}))?$/;

/**
 * Deutschen Betrag in ganze Cent wandeln, ohne Gleitkomma: „4,90“ → 490, „1.248,5“ → 124850.
 * Höchstens zwei Nachkommastellen, keine negativen Beträge.
 */
export function parseMoney(text: string): { ok: true; amountMinor: number } | { ok: false; message: string } {
  const cleaned = text.trim().replace(/\s*€$/, '');
  const m = MONEY_PATTERN.exec(cleaned);
  if (!m) return { ok: false, message: MONEY_INVALID_TEXT };
  const euros = m[1]!.replaceAll('.', '');
  const cents = (m[2] ?? '').padEnd(2, '0');
  const amountMinor = Number(`${euros}${cents}`);
  if (!Number.isSafeInteger(amountMinor)) return { ok: false, message: 'Der Betrag ist zu groß.' };
  return { ok: true, amountMinor };
}

/** Cent als deutscher Betrag ohne Währung: 124850 → „1.248,50“. */
export function formatMoneyAmount(amountMinor: number): string {
  const negative = amountMinor < 0;
  const digits = String(Math.abs(Math.trunc(amountMinor))).padStart(3, '0');
  const euros = digits.slice(0, -2).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${negative ? '-' : ''}${euros},${digits.slice(-2)}`;
}

/** Währungszeichen zum Anzeigen: EUR → „€“, sonst der Code. */
export function currencySymbol(currency: string): string {
  return currency === 'EUR' ? '€' : currency;
}

/** Geldbetrag zum Anzeigen: „4,90 €“. */
export function formatMoney(value: MoneyValue): string {
  return `${formatMoneyAmount(value.amountMinor)} ${currencySymbol(value.currency)}`;
}

export function isMoneyValue(value: unknown): value is MoneyValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    Number.isSafeInteger((value as MoneyValue).amountMinor) &&
    typeof (value as MoneyValue).currency === 'string'
  );
}

/** Leere Eingabe aus dem Formular (leerer Text) gilt als „nichts angegeben“. */
function blankToNull(value: unknown): unknown {
  return typeof value === 'string' && value.trim() === '' ? null : value;
}

function integerSchema(options: { required: boolean; min?: number | undefined; max?: number | undefined; unit?: string | undefined }) {
  const unit = options.unit ? ` ${options.unit}` : '';
  let number = z
    .number({ error: (issue) => (issue.input === undefined ? REQUIRED_TEXT : INTEGER_INVALID_TEXT) })
    .int(INTEGER_INVALID_TEXT);
  if (options.min !== undefined) number = number.min(options.min, `Bitte mindestens ${options.min}${unit} eingeben.`);
  if (options.max !== undefined) number = number.max(options.max, `Bitte höchstens ${options.max}${unit} eingeben.`);
  const target = options.required ? number : number.nullable();
  return z.preprocess((value) => {
    const v = blankToNull(value);
    if (v === null || v === undefined) return options.required ? undefined : null;
    if (typeof v === 'string' && /^-?\d+$/.test(v.trim())) return Number(v.trim());
    return v;
  }, target);
}

function moneySchema(currency: string, required: boolean) {
  const stored = z.object({ amountMinor: z.number().int().min(0), currency: z.literal(currency) }).strict();
  return z
    .union([z.string(), stored, z.null(), z.undefined()], { error: MONEY_INVALID_TEXT })
    .transform((value, ctx): MoneyValue | null => {
      if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
        if (required) {
          ctx.addIssue({ code: 'custom', message: REQUIRED_TEXT });
          return z.NEVER;
        }
        return null;
      }
      if (typeof value !== 'string') return value;
      const parsed = parseMoney(value);
      if (!parsed.ok) {
        ctx.addIssue({ code: 'custom', message: parsed.message });
        return z.NEVER;
      }
      return { amountMinor: parsed.amountMinor, currency };
    });
}

export const PERCENT_INVALID_TEXT = 'Bitte eine Zahl wie 20 oder 20,5 eingeben.';

const PERCENT_PATTERN = /^(-?)(\d{1,15})(?:[.,](\d{1,15}))?$/;

/** Dezimaltext → Einheiten von 10^-`scale` (ganzzahlig); `null`, wenn mehr Stellen oder kein Dezimaltext. */
function percentUnits(text: string, scale: number): bigint | null {
  const m = PERCENT_PATTERN.exec(text.trim());
  if (!m) return null;
  const frac = (m[3] ?? '').replace(/0+$/, '');
  if (frac.length > scale) return null;
  const units = BigInt(`${m[2]}${frac.padEnd(scale, '0')}`);
  return m[1] === '-' ? -units : units;
}

/**
 * Prozent-Eingabe prüfen und vereinheitlichen: „20,5“, „20.5“, „20 %“ oder Zahl → „20.5“. Höchstens
 * `fractionDigits` Nachkommastellen, `min` ≤ Wert < `maxExclusive`. Rein, ohne Gleitkomma.
 */
export function parsePercent(
  input: Extract<SettingInput, { kind: 'percent' }>,
  value: unknown,
): { ok: true; value: string } | { ok: false; message: string } {
  const invalid = input.invalidText ?? PERCENT_INVALID_TEXT;
  const unit = input.unit ?? '%';
  const raw = typeof value === 'number' && Number.isFinite(value) ? String(value) : typeof value === 'string' ? stripUnit(value.trim(), unit) : null;
  if (raw === null || raw === '') return { ok: false, message: raw === '' && !input.invalidText ? REQUIRED_TEXT : invalid };
  const units = percentUnits(raw, input.fractionDigits);
  if (units === null) return { ok: false, message: invalid };
  const min = input.min !== undefined ? percentUnits(input.min, input.fractionDigits) : null;
  const max = input.maxExclusive !== undefined ? percentUnits(input.maxExclusive, input.fractionDigits) : null;
  if ((min !== null && units < min) || (max !== null && units >= max)) return { ok: false, message: invalid };
  const div = 10n ** BigInt(input.fractionDigits);
  const abs = units < 0n ? -units : units;
  const frac = input.fractionDigits > 0 ? (abs % div).toString().padStart(input.fractionDigits, '0').replace(/0+$/, '') : '';
  return { ok: true, value: `${units < 0n ? '-' : ''}${abs / div}${frac ? `.${frac}` : ''}` };
}

function stripUnit(text: string, unit: string): string {
  return text.endsWith(unit) ? text.slice(0, -unit.length).trimEnd() : text;
}

/** Gespeicherter Prozentwert zum Anzeigen bzw. Bearbeiten: „99.9“ → „99,9“. */
export function formatPercentValue(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value).replace('.', ',') : '';
}

function cellSchema(column: TableColumn): z.ZodType {
  const required = column.required ?? false;
  switch (column.type) {
    case 'text': {
      const max = column.maxLength ?? 200;
      const text = z.string({ error: REQUIRED_TEXT }).trim().max(max, `Bitte höchstens ${max} Zeichen eingeben.`);
      return required ? text.min(1, REQUIRED_TEXT) : z.preprocess((v) => v ?? '', text);
    }
    case 'integer':
      return integerSchema({ required, min: column.min, max: column.max, unit: column.unit });
    case 'money':
      return moneySchema(column.currency, required);
    case 'select': {
      // Optionen erst beim Prüfen lesen: Module dürfen sie zur Laufzeit erweitern (z. B. angemeldete Belegarten, Story 3.4).
      const choice = z
        .string({ error: SELECT_INVALID_TEXT })
        .trim()
        .refine((v) => (v === '' ? !required : column.options.some((o) => o.value === v)), { error: (issue) => (issue.input === '' ? REQUIRED_TEXT : SELECT_INVALID_TEXT) });
      return z.preprocess((v) => (v === null || v === undefined ? '' : v), choice);
    }
  }
}

/** Anzeigetext eines gespeicherten Auswahl-Werts: Label der Option, leer → `emptyLabel` (Standard „–“), unbekannt → Wert. */
export function selectLabel(column: Extract<TableColumn, { type: 'select' }>, value: unknown): string {
  if (value === null || value === undefined || value === '') return column.emptyLabel ?? '–';
  return column.options.find((o) => o.value === value)?.label ?? String(value);
}

function buildSchema(input: SettingInput): z.ZodType {
  switch (input.kind) {
    case 'email':
      return z
        .string({ error: input.requiredMessage ?? 'Bitte gib eine E-Mail-Adresse ein.' })
        .trim()
        .min(1, input.requiredMessage ?? 'Bitte gib eine E-Mail-Adresse ein.')
        .max(254, EMAIL_INVALID_TEXT)
        .pipe(z.email({ error: EMAIL_INVALID_TEXT }));
    case 'text': {
      const max = input.maxLength ?? 500;
      const text = z.string({ error: REQUIRED_TEXT }).trim().max(max, `Bitte höchstens ${max} Zeichen eingeben.`);
      const checked = input.required ? text.min(1, REQUIRED_TEXT) : text;
      const format = input.format;
      if (!format) return checked;
      return checked.transform((value, ctx) => {
        const result = formatText(format, value);
        if (result.ok) return result.value;
        ctx.addIssue({ code: 'custom', message: TEXT_FORMAT_TEXTS[format] });
        return z.NEVER;
      });
    }
    case 'boolean':
      return z.boolean({ error: BOOLEAN_INVALID_TEXT });
    case 'integer':
      return integerSchema({ required: input.emptyLabel === undefined, min: input.min, max: input.max, unit: input.unit });
    case 'money':
      return moneySchema(input.currency, true);
    case 'select': {
      const allowed = new Set(input.options.map((o) => o.value));
      return z.string({ error: SELECT_INVALID_TEXT }).refine((v) => allowed.has(v), { error: SELECT_INVALID_TEXT });
    }
    case 'percent':
      return z.unknown().transform((value, ctx) => {
        const parsed = parsePercent(input, value);
        if (parsed.ok) return parsed.value;
        ctx.addIssue({ code: 'custom', message: parsed.message });
        return z.NEVER;
      });
    case 'currency':
      return z.string({ error: CURRENCY_INVALID_TEXT }).transform((value, ctx) => {
        const code = normalizeCurrencyCode(value);
        if (code === null) {
          ctx.addIssue({ code: 'custom', message: CURRENCY_INVALID_TEXT });
          return z.NEVER;
        }
        return code;
      });
    case 'user':
      return z.preprocess(
        (value) => (value === undefined ? null : blankToNull(value)),
        z.uuid({ error: USER_INVALID_TEXT }).nullable(),
      );
    case 'table': {
      const shape: Record<string, z.ZodType> = {};
      for (const column of input.columns) shape[column.key] = cellSchema(column);
      const maxRows = input.maxRows ?? 200;
      const rows = z.array(z.object(shape, { error: 'Jede Zeile braucht die Spalten der Tabelle.' }).strict(), {
        error: 'Bitte die Tabelle ausfüllen.',
      });
      const maxMessage = `Höchstens ${maxRows} Zeilen.`;
      if (!input.uniqueBy) return rows.max(maxRows, maxMessage);
      const unique = input.columns.find((c) => c.key === input.uniqueBy);
      // Programmfehler (z. B. Spalte umbenannt): nie still ohne Prüfung weitermachen.
      if (!unique) throw new Error(`uniqueBy: Spalte ${input.uniqueBy} gibt es in der Tabelle nicht.`);
      // Doppelte zuerst prüfen: zod ≥ 4.6.6 lässt Prüfungen nach einem Verstoß gegen „höchstens“ aus.
      return rows.superRefine((list, ctx) => {
        const seen = new Map<unknown, number>();
        list.forEach((row, i) => {
          const value = (row as Record<string, unknown>)[unique.key];
          if (value === '' || value === null || value === undefined) return;
          const first = seen.get(value);
          if (first === undefined) {
            seen.set(value, i);
            return;
          }
          const shown = unique.type === 'select' ? selectLabel(unique, value) : String(value);
          ctx.addIssue({ code: 'custom', path: [i, unique.key], message: `„${shown}“ steht schon in Zeile ${first + 1}; jeder Wert nur einmal.` });
        });
      }).max(maxRows, maxMessage);
    }
  }
}

const schemaCache = new WeakMap<SettingInput, z.ZodType>();

/** Zod-Schema einer Eingabeart (gleich in Command und Formular). */
export function schemaForInput(input: SettingInput): z.ZodType {
  let schema = schemaCache.get(input);
  if (!schema) {
    schema = buildSchema(input);
    schemaCache.set(input, schema);
  }
  return schema;
}

/** Fehler einer Prüfung als Sätze: `value` (erster Satz, bei Tabellen mit Zeile und Spalte) und je Zelle `value.<zeile>.<spalte>`. */
export function settingValueErrors(input: SettingInput, issues: readonly z.core.$ZodIssue[]): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const issue of issues) {
    const [row, column] = issue.path;
    let sentence = issue.message;
    if (input.kind === 'table' && typeof row === 'number') {
      const label = input.columns.find((c) => c.key === column)?.label;
      sentence = label ? `Zeile ${row + 1}, ${label}: ${issue.message}` : `Zeile ${row + 1}: ${issue.message}`;
      errors[['value', ...issue.path.map(String)].join('.')] ??= issue.message;
    }
    errors.value ??= sentence;
  }
  if (!errors.value) errors.value = 'Bitte prüfe den Wert.';
  return errors;
}

/** Prüft einen Wert gegen die Eingabeart: geprüfter (umgewandelter) Wert oder Fehler je Feld. */
export function checkSettingValue(
  input: SettingInput,
  value: unknown,
): { ok: true; value: unknown } | { ok: false; errors: Record<string, string> } {
  const parsed = schemaForInput(input).safeParse(value);
  if (parsed.success) return { ok: true, value: parsed.data };
  return { ok: false, errors: settingValueErrors(input, parsed.error.issues) };
}

/** Erster Fehler als Satz, oder `null`, wenn der Wert passt. */
export function settingValueError(input: SettingInput, value: unknown): string | null {
  const checked = checkSettingValue(input, value);
  return checked.ok ? null : (checked.errors.value ?? 'Bitte prüfe den Wert.');
}

/** „2027-02-01“ → „01.02.2027“. */
export function formatIsoDate(date: string): string {
  const [y, m, d] = date.split('-');
  return `${d}.${m}.${y}`;
}

/**
 * „Gültig ab“ prüfen (D2): ein Kalendertag ab heute (Europe/Berlin); rückwirkend nicht, damit der
 * Verlauf zeigt, was wirklich galt. `today` kommt als Eingabe (keine Uhr in dieser Datei). Seit Story 1.26 nur
 * noch für Tabellen mit eigenem Gültig-ab (Steuersätze, Versandkosten in `taxrules`); Einstellungen gelten ab dem Speichern.
 */
export function validFromError(validFrom: unknown, today: string): string | null {
  if (typeof validFrom !== 'string' || !isIsoDate(validFrom)) return VALID_FROM_INVALID_TEXT;
  if (validFrom < today) return `„Gültig ab“ darf nicht in der Vergangenheit liegen. Frühestens heute (${formatIsoDate(today)}).`;
  return null;
}
